import type { PeerMetadata } from '@playcastdotio/Messaging';
import {
  isSharedTierPromotion,
  QUALITY_TIER_SHADOWCAM,
  selectRecoveryQualityTier,
  selectSharedCaptureTier,
  type SharedTierInput,
  type QualityTier,
} from '@playcastdotio/PeerCore';
import { MobileTierPolicy } from '../../../../../../../libs/PeerCore/src/mobileTier/MobileTierPolicy';

export type ShadowcamTierLogger = (message: string, detail: Record<string, unknown>) => void;

/**
 * Chooses which ShadowCam a guest streams from, and records the evidence behind the choice.
 *
 * Two questions, two entry points: what a guest starts on (from the hint it reported) and
 * what a guest comes back on after losing its connection (from the camera in use). Both log,
 * because the inputs are remote and lossy — a signaling server older than the
 * `connectionQuestionable` schema strips the hint on the way through, which is
 * indistinguishable at the host from a healthy link.
 *
 * Log messages keep the `[PeerCoordinator]` prefix: it names the subsystem these decisions
 * belong to, and existing traces and tests key off it.
 */
export class ShadowcamTierSelector {
  private readonly log: ShadowcamTierLogger;
  private readonly mobileTier = new MobileTierPolicy();

  constructor(log?: ShadowcamTierLogger) {
    this.log = log ?? ((message, detail) => console.log(message, detail));
  }

  /**
   * Treat Apple guests with a valid reported screen short edge at most 768 CSS
   * pixels as mobile, overriding isMobile: false. Otherwise return the explicit
   * isMobile flag, or undefined when it is absent.
   */
  public effectiveIsMobile(metadata?: PeerMetadata): boolean | undefined {
    if (this.isApplePhoneScreen(metadata)) return true;
    if (metadata?.isMobile === true) return true;
    if (metadata?.isMobile === false) return false;
    return undefined;
  }

  public selectInitialShadowcam(metadata?: PeerMetadata, retainedTier?: QualityTier): string {
    const isMobile = this.effectiveIsMobile(metadata);
    const selection = this.mobileTier.select({
      device: isMobile === true ? 'mobile' : isMobile === false ? 'desktop' : 'unknown',
      connectionQuestionable: metadata?.connectionQuestionable,
      retainedTier,
    });
    const shadowcam = QUALITY_TIER_SHADOWCAM[selection.tier];

    this.log('[PeerCoordinator] Initial quality tier', {
      tier: selection.tier,
      shadowcam,
      resolution: `${selection.maxWidth}x${selection.maxHeight}`,
      deviceClass: selection.device,
      reason: selection.reason,
      // This is a requested ceiling; sender verification is a separate observation.
      dimensionsVerified: false,
      isMobile: metadata?.isMobile ?? 'absent',
      connectionQuestionable: metadata?.connectionQuestionable ?? 'absent',
    });

    return shadowcam;
  }

  /**
   * Tier for a guest re-establishing its connection: step down from the camera in use,
   * retaining any stricter quality floor established for this peer.
   *
   * Returns null when the camera in use sits outside the tier ladder and there is therefore
   * no step to take from it — callers fall back to the initial selection.
   */
  public selectRecoveryShadowcam(
    metadata: PeerMetadata | undefined,
    currentShadowcam: string | null,
    retainedTier?: QualityTier,
  ): string | null {
    const selection = selectRecoveryQualityTier({
      isMobile: this.effectiveIsMobile(metadata) === true,
      currentShadowcam,
      retainedTier,
    });

    this.log('[PeerCoordinator] Recovery quality tier', {
      fromTier: selection.fromTier ?? 'unknown',
      toTier: selection.tier ?? 'unknown',
      fromShadowcam: currentShadowcam ?? 'none',
      toShadowcam: selection.shadowcam ?? 'fallback-to-initial',
      demoted: selection.demoted,
      reason: selection.reason,
      isMobile: metadata?.isMobile ?? 'absent',
      connectionQuestionable: metadata?.connectionQuestionable ?? 'absent',
    });

    return selection.shadowcam;
  }

  /** Apple phone screens only: short edge 320–768 CSS pixels. Smaller reports are not a phone. */
  private isApplePhoneScreen(metadata?: PeerMetadata): boolean {
    if (metadata?.isApple !== true) return false;
    const width = metadata.screenWidth;
    const height = metadata.screenHeight;
    if (typeof width !== 'number' || typeof height !== 'number') return false;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false;
    const shortEdge = Math.min(width, height);
    return shortEdge >= 320 && shortEdge <= 768;
  }

  /**
   * Lowest-common-denominator camera for every remaining guest on the shared capture.
   * Empty set → null (keep whatever is already captured).
   */
  public selectSharedShadowcam(peers: ReadonlyArray<SharedTierInput>): string | null {
    const selection = selectSharedCaptureTier(peers);
    if (!selection) return null;

    this.log('[PeerCoordinator] Shared quality tier', {
      tier: selection.tier,
      shadowcam: selection.shadowcam,
      resolution: `${selection.resolution.width}x${selection.resolution.height}`,
      peerCount: peers.length,
    });

    return selection.shadowcam;
  }

  /** True when stepping the shared camera up the ladder, never down. */
  public shouldPromoteSharedShadowcam(
    currentShadowcam: string | null,
    neededShadowcam: string | null,
  ): boolean {
    return isSharedTierPromotion(currentShadowcam, neededShadowcam);
  }
}
